import React, { useMemo } from 'react';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { Avatar } from 'components/layout/Avatar';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListColumnCheckIcon from 'components/lists/columns/ListColumnCheckIcon';

const UserHourList: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const params = new URLSearchParams(location.search);
    const weekStart = params.get("week_start");

    // Specify the columns of this list
    const columns: ListColumnType[] = useMemo(() => [
        {
            width: '2fr',
            field: 'full_name',
            label: 'timesheets.general.assignee_name_label',
            render: (_, row) => renderUserWithAvatar(row.full_name, row.profile_picture),
            disableOrdering: true,
        },
        {
            width: '54px',
            field: 'total_hours',
            label: 'timesheets.general.hours_label',
            render: (_, row) => renderHourValue(row.total_hours),
            disableOrdering: true,
        },
        {
            width: '3fr',
            field: 'hours_visual',
            label: '',
            render: (_, row) => renderHoursBar(row.productive_hours, row.internal_hours, row.scheduled_hours),
            disableOrdering: true,
        },
        {
            width: '84px',
            field: 'scheduled_hours',
            label: 'timesheets.general.scheduled_hours_label',
            render: (_, row) => renderHourValue(row.scheduled_hours),
            disableOrdering: true,
        },
        {
            width: '92px',
            field: 'overtime_hours',
            label: 'timesheets.general.overtime_hours_label',
            render: (_, row) => renderOvertime(row.overtime_hours),
            disableOrdering: true,
        },
        {
            width: '36px',
            field: 'approved',
            label: '',
            render: (_, row) => <ListColumnCheckIcon checked={row.approved} tooltipText='timesheets.general.timesheet_approved_tooltip' />,
            disableOrdering: true,
        }
    ], []);

    // Render the user name with avatar
    const renderUserWithAvatar = (full_name: string, profilePicture?: string | null) => {
        const hasAvatar = !!profilePicture || !!full_name;
        return (
            <div className={`name-with-avatar ${hasAvatar ? 'has-avatar' : ''}`}>
                {profilePicture ? (
                    <div
                        className="profile-image"
                        style={{ backgroundImage: `url(${profilePicture})` }}
                    />
                ) : (
                    <Avatar name={full_name} size="2rem" />
                )}
                <span className='name'>{full_name}</span>
            </div>
        );
    };

    // Render the hour value
    const renderHourValue = (hours: number | null | undefined) => {
        if (hours === null || hours === undefined) return <></>;
        return (
            <span className='text-right'>{formatHours(hours)}</span>
        );
    }

    // Render the overtime above the scheduled hours, highlighted when there is any
    const renderOvertime = (hours: number | null | undefined) => {
        if (hours === null || hours === undefined) return <></>;
        return (
            <span className={`text-right ${Number(hours) > 0 ? 'overtime-value' : ''}`}>{formatHours(hours)}</span>
        );
    };

    // Format the total as hours
    const formatHours = (hours: number | null | undefined) => {
        if (hours === null || hours === undefined) return <></>;

        const totalMinutes = Math.round(hours * 60);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;

        return `${h}:${m.toString().padStart(2, '0')}`;
    };

    // Render the hours bar, scaled on the scheduled hours when the user has a timetable
    const renderHoursBar = (productiveHours: number, internalHours: number, scheduledHours?: number | null) => {

        // Get the productive and internal hours
        const productive = Number(productiveHours || 0);
        const internal = Number(internalHours || 0);
        const total = productive + internal;

        // Get the scheduled hours of the week
        const scheduled = scheduledHours !== null && scheduledHours !== undefined ? Number(scheduledHours) : null;

        // Set the max hours for the bar, the scheduled hours or the total above it
        const maxHours = scheduled && scheduled > 0 ? Math.max(scheduled, total) : 48;

        // Calculate the position of the scheduled hours marker and the overtime segment
        const scheduledPosition = scheduled && scheduled > 0 ? (scheduled / maxHours) * 100 : null;
        const overtimeWidth = scheduledPosition !== null && total > scheduled! ? ((total - scheduled!) / maxHours) * 100 : 0;

        // Calculate the width of each bar
        const productiveWidth = Math.min((productive / maxHours) * 100, 100);
        const internalWidth = Math.min((internal / maxHours) * 100, 100);

        // Calculate the percentages
        const productivePercentage = Math.round((productive / maxHours) * 100);
        const internalPercentage = Math.round((internal / maxHours) * 100);

        return (
            <div className="hours-bar">
                {productive > 0 && (
                    <div className="hours-bar-productive"
                        style={{ 
                            width: `${productiveWidth}%`,
                            left: 0
                        }}
                    >
                        <span className="tooltip tooltip-productive">{t('timesheets.general.productive_hours_tooltip', { hours: formatHours(productive), percentage: productivePercentage })}</span>
                    </div>
                )}
                {internal > 0 && (
                <div className="hours-bar-internal"
                     style={{ 
                        width: `${internalWidth}%`,
                        left: `${productiveWidth}%` 
                     }}
                >
                    <span className="tooltip tooltip-internal">{t('timesheets.general.internal_hours_tooltip', { hours: formatHours(internal), percentage: internalPercentage })}</span>
                </div>
                )}
                {overtimeWidth > 0 && (
                    <div className="hours-bar-overtime"
                         style={{ width: `${overtimeWidth}%`, left: `${scheduledPosition}%` }}>
                        <span className="tooltip tooltip-overtime">{t('timesheets.general.overtime_hours_tooltip', { hours: formatHours(total - scheduled!) })}</span>
                    </div>
                )}
                {scheduledPosition !== null && (
                    <div className="hours-bar-scheduled-marker" style={{ left: `${scheduledPosition}%` }} />
                )}
                <div className="hours-bar-grid">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="hours-bar-grid-cell" />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <AbstractList
            query={{
                endpoint: 'get_user_hours_list',
                defaultParams: { },
                object: 'user_hours'
            }}
            headerTitle='timesheets.general.timesheets_header'
            objectName='timesheets.general.object_name'
            tableClassName="user-hours-table"
            columns={columns}
            showAddButton={false}
            isPaginated={false}
            onRowClick={(row) => history.push(`/user-hours/timerows?user_id=${row.id}&week_start=${weekStart}`)}
            showPerWeek
        />
    );
};

export default UserHourList;